import { Collapse, Grid, Table, TableCheckType, Text } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { useAtom, useAtomValue } from 'jotai';
import moment from 'moment';
import { memo, useCallback, useMemo } from 'react';
import { NetSuiteMatchingLine, useNetSuiteLineFieldSettings, useNetSuiteMatchingLines } from 'shared/hooks/(matchings)';
import { useNetSuiteCompanyCustomFields, useNetSuiteNewLineCustomFieldsDefinitions } from 'shared/hooks/(requests)';

import { getColumnDefinitions } from '../../NetSuiteMatchingPopup.config';
import { isLineMatchingDisabled } from '../../NetSuiteMatchingPopup.helpers';
import { netSuiteSourceMatchingLineState, netSuiteTargetMatchingLineState } from '../../NetSuiteMatchingPopup.states';
import { NetSuiteSourceLine } from '../../NetSuiteMatchingPopup.types';
import { checkboxColumnCover } from './MatchingLineItems.constants';
import { MatchingLineItemsProps } from './MatchingLineItems.types';

export const MatchingLineItems = memo((props: MatchingLineItemsProps) => {
    const { request, lineType, matchingInfo, currencyISOCode } = props;

    const [targetLine, setTargetLine] = useAtom(netSuiteTargetMatchingLineState);

    const sourceLine = useAtomValue(netSuiteSourceMatchingLineState);

    const { netSuiteCompanyCustomFields } = useNetSuiteCompanyCustomFields(request.companyId);

    const fieldSettings = useNetSuiteLineFieldSettings(request.companyId, request.integrationCode, lineType);

    const lines = useNetSuiteMatchingLines({
        matchingLinesInfos: [matchingInfo],
        lineType,
        lineNumber: matchingInfo.documentLineNumber,
        request,
    });

    const checkedItems = useMemo(
        () => (targetLine && lines.some(({ id }) => id === targetLine.id) ? [targetLine.id] : []),
        [lines, targetLine]
    );

    const customFieldsColumnDefinitions = useNetSuiteNewLineCustomFieldsDefinitions<
        NetSuiteSourceLine | NetSuiteMatchingLine
    >(lines, netSuiteCompanyCustomFields);

    const columnDefinitions = useMemo(
        () =>
            getColumnDefinitions({
                lineType,
                fieldSettings,
                customFieldsColumnDefinitions,
                sourceLine,
            }),
        [lineType, fieldSettings, customFieldsColumnDefinitions, sourceLine]
    );

    const onCheckedItemsChange = useCallback(
        (checkedItems: string[]) => {
            const checkedItemId = checkedItems[0];
            const line = lines.find(({ id }) => id === checkedItemId);

            line && setTargetLine(line);
        },
        [lines, setTargetLine]
    );

    const getIsDisabledRow = useCallback(
        (line: NetSuiteMatchingLine) => isLineMatchingDisabled(line, lineType, sourceLine),
        [lineType, sourceLine]
    );

    return (
        <Collapse
            name={matchingInfo.friendlyName ?? ''}
            headerContent={
                <Grid gridTemplateColumns='auto 240px' gap={12} alignItems='center'>
                    <Text font='label' fontSize='large' color='midnight80' spacing='0 12'>
                        {moment.utc(matchingInfo.date).format('ll')}
                    </Text>

                    <Text font='label' fontSize='large' color='midnight80' textAlign='right'>
                        {intl.formatCurrency(matchingInfo.totalAmount, currencyISOCode ?? request.currency)}
                    </Text>
                </Grid>
            }
            collapsed
            color='white100'
            spacing='0 0 8 24'
        >
            <Table
                columns={columnDefinitions}
                data={lines}
                hideBorderLastRow
                headerColor='transparent'
                hideEmptyColumns
                checkedItems={checkedItems}
                onCheckedItemsChange={onCheckedItemsChange}
                checkType={TableCheckType.radio}
                checkboxColumnCover={checkboxColumnCover}
                hoverable={false}
                getIsDisabledRow={getIsDisabledRow}
                columnSpacing='8'
            />
        </Collapse>
    );
});

MatchingLineItems.displayName = 'MatchingLineItems';
